import React, { memo, Fragment, useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react'
import { AuthContext } from '@/context/auth-context'
import { GuideContext } from '@/context/guide-context'
import { gql, useQuery, useMutation } from '@apollo/client'
import { USER_FIELDS, CORE_USER_FIELDS } from '@/graphql/user-fragments'
import { Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap'
import { Elements } from '@stripe/react-stripe-js'
import { stripe, appearance } from '@/utils/stripe'
import { useForm } from '@/utils/form-hook'
import { googleMapsPlaces } from '@/utils/google-maps-places'
import PaymentDetails from '@/components/users/PaymentDetails'
import { showNotification } from '@/utils/notifications'
import { ToastContainer } from 'react-toastify'
import Card from '@/components/common/Card'
import ShowErrors from '@/components/partials/ShowErrors'
import { CubeLoader } from '@/components/ui/cube-loader'
import _ from 'lodash'
import { CardIcon } from '@/components/icons/new'
import { First } from 'react-bootstrap/esm/PageItem'

const GET_USER = gql`
    ${USER_FIELDS}
    query Users($where: UserWhere) {
        users(where: $where) {
            campaignsAggregate {
                count
            }
            ...UserFields
        }
    }
`

const UPDATE_USER = gql`
    ${CORE_USER_FIELDS}
    mutation UpdateUser($id: ID!, $userInput: UserInput) {
        updateUser(id: $id, userInput: $userInput) {
            ...CoreUserFields
        }
    }
`

const BILLING_FIELD_DEFAULTS = {
    firstName: '',
    lastName: '',
    businessName: '',
    address: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    timeZoneOffset: null,
    location: null,
    logoImage: null,
    notes: '',
}

const mapUserDataToBillingValues = (userData) => {
    if (!userData) return BILLING_FIELD_DEFAULTS

    return Object.keys(BILLING_FIELD_DEFAULTS).reduce((acc, key) => {
        if (key === 'logoImage') {
            acc[key] = null
            return acc
        }
        acc[key] = userData[key] ?? BILLING_FIELD_DEFAULTS[key]
        return acc
    }, {})
}

const AdminBilling = memo(() => {
    const { user } = useContext(AuthContext)
    const { guide } = useContext(GuideContext)
    const [errors, setError] = useState([])
    const [success, setSuccess] = useState(false)
    const addressInputRef = useRef(null)
    const guidedPaymentRef = useRef(false)
    const stripeCardFormRef = useRef(null)

    const { data, loading } = useQuery(GET_USER, {
        variables: { where: { id: user.id } },
    })

    const userData = useMemo(() => data?.users?.[0], [data])

    const billingInitialValues = useMemo(() => {
        return mapUserDataToBillingValues(userData)
    }, [userData])

    const handleFocusCardNumber = useCallback(() => {
        if (stripeCardFormRef.current) {
            stripeCardFormRef.current.focusCardNumber()
        }
    }, [])

    const [updateUser, mutation] = useMutation(UPDATE_USER, {
        onError({ graphQLErrors }) {
            setError(graphQLErrors)
        },
        onCompleted: () => {
            resetBillingValidation()
            setError([])
            setSuccess(true)
        },
    })

    const handleBillingSubmit = useCallback(
        async (formValues) => {
            if (!formValues.country) {
                if (addressInputRef.current) {
                    addressInputRef.current.setCustomValidity('Address was not recognised')
                }
                return
            }

            await updateUser({
                variables: {
                    id: user.id,
                    userInput: _.omit(formValues, ['location']),
                },
            })
        },
        [updateUser, user.id],
    )

    const {
        onChange: onBillingChange,
        onSubmit: onBillingSubmit,
        values: billingValues,
        validated: billingValidated,
        setValue: setBillingValue,
        resetValidation: resetBillingValidation,
    } = useForm(() => handleBillingSubmit(billingValues), billingInitialValues)

    useEffect(() => {
        if (userData) {
            const mappedValues = mapUserDataToBillingValues(userData)
            setBillingValue(mappedValues)
        }
    }, [userData, setBillingValue])

    useEffect(() => {
        if (!billingValues.location) return

        setBillingValue((prevState) => ({
            ...prevState,
            address: billingValues.location.address,
            addressLine1: billingValues.location.line1,
            addressLine2: billingValues.location.line2,
            city: billingValues.location.suburb,
            state: billingValues.location.state,
            postalCode: billingValues.location.postcode,
            country: billingValues.location.country,
            timeZoneOffset: billingValues.location.offset * 60,
        }))
    }, [billingValues.location, setBillingValue])

    useEffect(() => {
        if (!loading && addressInputRef.current) {
            googleMapsPlaces(setBillingValue, addressInputRef.current)
        }
    }, [loading, setBillingValue])

    useEffect(() => {
        if (!loading && addressInputRef.current) {
            const isValid = billingValues.timeZoneOffset !== null
            addressInputRef.current.setCustomValidity(isValid ? '' : 'Address was not defined')
        }
    }, [loading, billingValues.timeZoneOffset])

    useEffect(() => {
        if (!userData || guide !== 'on' || guidedPaymentRef.current) {
            return
        }

        const shouldShowNotification = !userData.paymentMethodId && userData.address && userData.phone

        if (shouldShowNotification) {
            const stepElement = document.querySelector('.step-4')
            if (stepElement) {
                stepElement.click()
            }

            showNotification(
                <div>
                    <p>Please enter your credit card details and click "Update."</p>
                    <p>
                        Your card will only be charged when you create a new campaign. The cost is $1,770 AUD plus
                        surcharge per campaign per year.
                    </p>
                </div>,
                'info',
                false,
                'Continue',
                handleFocusCardNumber,
                false,
            )
            guidedPaymentRef.current = true
        }
    }, [userData, guide, handleFocusCardNumber])

    if (loading) {
        return <CubeLoader size="default" fullScreen={true} />
    }

    if (!userData) {
        return null
    }

    return (
        <Fragment>
            <ToastContainer />
            <Card className="page-card">
                <Card.Header className="d-flex justify-content-between">
                    <div className="d-flex align-items-center justify-content-center">
                        <i className="icon text-gray-200 me-1">
                            <CardIcon size={24} />
                        </i>
                        <h5 className="mb-0 text-white">Billing Details</h5>
                    </div>
                </Card.Header>

                <Card.Body>
                    <Row>
                        <Col lg="4" md="6" sm="12">
                            <Elements stripe={stripe} options={appearance}>
                                <PaymentDetails userData={userData} data={userData} ref={stripeCardFormRef} />
                            </Elements>
                        </Col>

                        <Col lg="8" md="6" sm="12">
                            <Form noValidate validated={billingValidated}>
                                <h5 className="mb-3 text-white text-lg">Billing</h5>

                                <div className="row">
                                    <Form.Group className="col-md-12 form-group step-1-1">
                                        <Form.Label htmlFor="bname" className="tw:!text-sm">
                                            Business Name
                                        </Form.Label>
                                        <Form.Control
                                            required
                                            type="text"
                                            id="bname"
                                            placeholder="Enter Business Name"
                                            name="businessName"
                                            value={billingValues.businessName || ''}
                                            onChange={onBillingChange}
                                        />
                                    </Form.Group>

                                    <Form.Group className="col-md-12 form-group step-2">
                                        <Form.Label htmlFor="address" className="tw:!text-sm">
                                            Business Address
                                        </Form.Label>
                                        <Form.Control
                                            required
                                            type="text"
                                            id="address"
                                            ref={addressInputRef}
                                            placeholder="Enter Address"
                                            name="address"
                                            value={billingValues.address || ''}
                                            onChange={onBillingChange}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {billingValues.address?.length > 0 ? 'Address was not recognised' : ''}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </div>

                                <div className="row">
                                    <Form.Group className="col-md-12 form-group">
                                        <Form.Label htmlFor="notes" className="tw:!text-sm">
                                            Notes
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            id="notes"
                                            rows="3"
                                            name="notes"
                                            value={billingValues.notes || ''}
                                            onChange={onBillingChange}
                                        />
                                    </Form.Group>
                                </div>

                                <div className="pb-3">
                                    <ShowErrors errors={errors} />
                                    <Alert
                                        variant="success alert-left alert-dismissible fade show mb-3"
                                        role="alert"
                                        show={success}
                                        onClose={() => setSuccess(false)}
                                        dismissible
                                    >
                                        <span> Billing details have been successfully updated </span>
                                    </Alert>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                </Card.Body>

                <Card.Footer className="d-flex justify-content-end">
                    <Button
                        type="button"
                        variant="btn btn-primary"
                        onClick={onBillingSubmit}
                        disabled={mutation.loading}
                    >
                        {mutation.loading ? (
                            <Fragment>
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />{' '}
                                Saving...
                            </Fragment>
                        ) : (
                            'Save'
                        )}
                    </Button>
                </Card.Footer>
            </Card>
        </Fragment>
    )
})

AdminBilling.displayName = 'AdminBilling'

export default AdminBilling
